export interface ContentField {
    key: string;
    label: string;
    default: string;
    multiline?: boolean;
}
export interface ContentSection {
    title: string;
    fields: ContentField[];
}

export const CONTENT_SECTIONS: ContentSection[] = [
    {
        title: "Hero (oben)",
        fields: [
            { key: "hero.eyebrow", label: "Kleine Zeile oben", default: "TCG Awards 2026" },
            { key: "hero.title", label: "Überschrift", default: "Vote for the people, products and moments shaping TCG culture.", multiline: true },
            { key: "hero.text", label: "Einleitungstext", default: "A global award platform for Trading Card Games, built to recognize excellence across games, creators, events, stores, industry partners and competitive performance.", multiline: true },
        ],
    },
    {
        title: "Vote Process",
        fields: [
            { key: "process.eyebrow", label: "Kleine Zeile", default: "Vote Timeline" },
            { key: "process.title", label: "Überschrift", default: "How the voting process works" },
            { key: "process.text", label: "Beschreibung", default: "The flow is simple: nominate strong entries, narrow them into a shortlist, let the community vote and celebrate the winners.", multiline: true },
        ],
    },
    {
        title: "Award Categories",
        fields: [
            { key: "categories.eyebrow", label: "Kleine Zeile", default: "Award Categories" },
            { key: "categories.title", label: "Überschrift", default: "All official categories" },
            { key: "categories.text", label: "Beschreibung", default: "Every real category from the current TCG Awards structure is included, grouped by voting model.", multiline: true },
        ],
    },
    {
        title: "About the Awards",
        fields: [
            { key: "about.eyebrow", label: "Kleine Zeile", default: "About the Awards" },
            { key: "about.title", label: "Überschrift", default: "A clearer stage for a growing ecosystem" },
            { key: "about.text", label: "Beschreibung", default: "The TCG Awards recognize the people and organizations that move Trading Card Games forward.", multiline: true },
        ],
    },
    {
        title: "Business Case",
        fields: [
            { key: "business.eyebrow", label: "Kleine Zeile", default: "Jury Categories" },
            { key: "business.title", label: "Überschrift", default: "Submit a Business Case" },
            { key: "business.text", label: "Beschreibung", default: "Companies, agencies, publishers and service providers can submit business cases for jury-based categories such as Brand Partnership, Campaign and Service Provider of the Year.", multiline: true },
        ],
    },
    {
        title: "News & Socials",
        fields: [
            { key: "news.eyebrow", label: "Kleine Zeile", default: "News & Socials" },
            { key: "news.title", label: "Überschrift", default: "Latest Updates" },
            { key: "news.text", label: "Beschreibung", default: "Aktuelle Updates, Highlights und Beiträge rund um die TCG Awards.", multiline: true },
        ],
    },
    {
        title: "Newsletter",
        fields: [
            { key: "newsletter.eyebrow", label: "Kleine Zeile", default: "Stay Updated" },
            { key: "newsletter.title", label: "Überschrift", default: "Follow the road to the winners" },
            { key: "newsletter.text", label: "Beschreibung", default: "Get updates around nominations, shortlist announcements, community voting and the final award show.", multiline: true },
        ],
    },
];

// Liefert den gespeicherten Wert oder den Standardtext.
export function contentValue(content: Record<string, string>, key: string): string {
    if (content[key] !== undefined && content[key] !== "") return content[key];
    for (const section of CONTENT_SECTIONS) {
        const field = section.fields.find((f) => f.key === key);
        if (field) return field.default;
    }
    return "";
}

// ─── Abschnitte des Onepagers (sortier- und ausblendbar) ──────────────────
export interface LayoutSection {
    key: string;
    label: string;
}

// Reihenfolge hier = Standard-Reihenfolge der Startseite.
export const MANAGED_SECTIONS: LayoutSection[] = [
    { key: "introStrip", label: "Intro-Streifen" },
    { key: "aboutAwards", label: "About the Awards" },
    { key: "voteProcess", label: "Vote Process" },
    { key: "awardCategories", label: "Award Categories" },
    { key: "businessCase", label: "Business Case" },
    { key: "partners", label: "Partner / Sponsoren" },
    { key: "newsSocials", label: "News & Socials" },
    { key: "newsletter", label: "Newsletter" },
];

export interface LayoutEntry {
    key: string;
    visible: boolean;
}

export function defaultLayout(): LayoutEntry[] {
    return MANAGED_SECTIONS.map((s) => ({ key: s.key, visible: true }));
}

// Liest die gespeicherte Layout-Konfiguration (JSON) robust ein:
// unbekannte Abschnitte werden ignoriert, fehlende hinten ergänzt.
export function parseLayout(raw: string | undefined): LayoutEntry[] {
    const def = defaultLayout();
    if (!raw) return def;
    try {
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return def;
        const known = new Set(MANAGED_SECTIONS.map((s) => s.key));
        const result: LayoutEntry[] = parsed
            .filter((e) => e && known.has(e.key))
            .map((e) => ({ key: e.key, visible: e.visible !== false }));
        for (const s of MANAGED_SECTIONS) {
            if (!result.find((e) => e.key === s.key)) result.push({ key: s.key, visible: true });
        }
        return result;
    } catch {
        return def;
    }
}
